import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeName = 'dark-purple' | 'charcoal-blue' | 'deep-teal' | 'slate-gray' | 'navy-blue';

interface ThemeColors {
  background: string;
  foreground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;
  muted: string;
  mutedForeground: string;
  card: string;
  cardForeground: string;
  border: string;
  input: string;
  ring: string;
  gradientHero: string;
  gradientCard: string;
  shadowSm: string;
  shadowCard: string;
  shadowLg: string;
}

const themes: Record<ThemeName, { name: string; colors: ThemeColors }> = {
  'dark-purple': {
    name: 'Dark Purple',
    colors: {
      background: '260 25% 8%',
      foreground: '0 0% 98%',
      primary: '266 83% 67%',
      primaryForeground: '0 0% 100%',
      secondary: '158 64% 52%',
      secondaryForeground: '0 0% 100%',
      accent: '260 20% 12%',
      accentForeground: '0 0% 95%',
      muted: '260 15% 15%',
      mutedForeground: '0 0% 70%',
      card: '260 20% 10%',
      cardForeground: '0 0% 95%',
      border: '260 15% 18%',
      input: '260 15% 15%',
      ring: '266 83% 67%',
      gradientHero: 'linear-gradient(135deg, hsl(260 60% 20%) 0%, hsl(280 70% 25%) 25%, hsl(200 70% 30%) 100%)',
      gradientCard: 'linear-gradient(180deg, hsl(260 20% 12%) 0%, hsl(260 15% 10%) 100%)',
      shadowSm: '0 1px 2px 0 hsl(260 50% 5% / 0.3)',
      shadowCard: '0 4px 6px -1px hsl(260 50% 5% / 0.4)',
      shadowLg: '0 10px 15px -3px hsl(260 50% 5% / 0.5)',
    }
  },
  'charcoal-blue': {
    name: 'Charcoal Blue-Gray',
    colors: {
      background: '210 15% 12%',
      foreground: '0 0% 98%',
      primary: '266 83% 67%',
      primaryForeground: '0 0% 100%',
      secondary: '158 64% 52%',
      secondaryForeground: '0 0% 100%',
      accent: '210 15% 16%',
      accentForeground: '0 0% 95%',
      muted: '210 15% 18%',
      mutedForeground: '0 0% 70%',
      card: '210 15% 14%',
      cardForeground: '0 0% 95%',
      border: '210 15% 20%',
      input: '210 15% 16%',
      ring: '266 83% 67%',
      gradientHero: 'linear-gradient(135deg, hsl(210 20% 18%) 0%, hsl(220 25% 22%) 25%, hsl(200 70% 30%) 100%)',
      gradientCard: 'linear-gradient(180deg, hsl(210 15% 16%) 0%, hsl(210 15% 12%) 100%)',
      shadowSm: '0 1px 2px 0 hsl(210 20% 6% / 0.3)',
      shadowCard: '0 4px 6px -1px hsl(210 20% 6% / 0.4)',
      shadowLg: '0 10px 15px -3px hsl(210 20% 6% / 0.5)',
    }
  },
  'deep-teal': {
    name: 'Deep Teal',
    colors: {
      background: '185 30% 8%',
      foreground: '0 0% 98%',
      primary: '266 83% 67%',
      primaryForeground: '0 0% 100%',
      secondary: '158 64% 52%',
      secondaryForeground: '0 0% 100%',
      accent: '185 25% 12%',
      accentForeground: '0 0% 95%',
      muted: '185 20% 15%',
      mutedForeground: '0 0% 70%',
      card: '185 25% 10%',
      cardForeground: '0 0% 95%',
      border: '185 20% 18%',
      input: '185 20% 15%',
      ring: '266 83% 67%',
      gradientHero: 'linear-gradient(135deg, hsl(185 35% 15%) 0%, hsl(195 40% 20%) 25%, hsl(200 70% 30%) 100%)',
      gradientCard: 'linear-gradient(180deg, hsl(185 25% 12%) 0%, hsl(185 25% 10%) 100%)',
      shadowSm: '0 1px 2px 0 hsl(185 30% 5% / 0.3)',
      shadowCard: '0 4px 6px -1px hsl(185 30% 5% / 0.4)',
      shadowLg: '0 10px 15px -3px hsl(185 30% 5% / 0.5)',
    }
  },
  'slate-gray': {
    name: 'Slate Gray',
    colors: {
      background: '220 20% 10%',
      foreground: '0 0% 98%',
      primary: '266 83% 67%',
      primaryForeground: '0 0% 100%',
      secondary: '158 64% 52%',
      secondaryForeground: '0 0% 100%',
      accent: '220 18% 14%',
      accentForeground: '0 0% 95%',
      muted: '220 15% 16%',
      mutedForeground: '0 0% 70%',
      card: '220 18% 12%',
      cardForeground: '0 0% 95%',
      border: '220 15% 19%',
      input: '220 15% 16%',
      ring: '266 83% 67%',
      gradientHero: 'linear-gradient(135deg, hsl(220 22% 16%) 0%, hsl(225 25% 20%) 25%, hsl(200 70% 30%) 100%)',
      gradientCard: 'linear-gradient(180deg, hsl(220 18% 14%) 0%, hsl(220 18% 12%) 100%)',
      shadowSm: '0 1px 2px 0 hsl(220 20% 6% / 0.3)',
      shadowCard: '0 4px 6px -1px hsl(220 20% 6% / 0.4)',
      shadowLg: '0 10px 15px -3px hsl(220 20% 6% / 0.5)',
    }
  },
  'navy-blue': {
    name: 'Navy Blue',
    colors: {
      background: '215 35% 10%',
      foreground: '0 0% 98%',
      primary: '266 83% 67%',
      primaryForeground: '0 0% 100%',
      secondary: '158 64% 52%',
      secondaryForeground: '0 0% 100%',
      accent: '215 30% 14%',
      accentForeground: '0 0% 95%',
      muted: '215 25% 16%',
      mutedForeground: '0 0% 70%',
      card: '215 30% 12%',
      cardForeground: '0 0% 95%',
      border: '215 25% 19%',
      input: '215 25% 16%',
      ring: '266 83% 67%',
      gradientHero: 'linear-gradient(135deg, hsl(215 40% 16%) 0%, hsl(220 45% 20%) 25%, hsl(200 70% 30%) 100%)',
      gradientCard: 'linear-gradient(180deg, hsl(215 30% 14%) 0%, hsl(215 30% 12%) 100%)',
      shadowSm: '0 1px 2px 0 hsl(215 35% 6% / 0.3)',
      shadowCard: '0 4px 6px -1px hsl(215 35% 6% / 0.4)',
      shadowLg: '0 10px 15px -3px hsl(215 35% 6% / 0.5)',
    }
  },
};

interface ThemeContextType {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themes: typeof themes;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    const saved = localStorage.getItem('checkfirst-theme');
    return (saved as ThemeName) || 'charcoal-blue';
  });

  useEffect(() => {
    const root = document.documentElement;
    const colors = themes[theme].colors;

    // Update CSS variables
    root.style.setProperty('--background', colors.background);
    root.style.setProperty('--foreground', colors.foreground);
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-foreground', colors.primaryForeground);
    root.style.setProperty('--secondary', colors.secondary);
    root.style.setProperty('--secondary-foreground', colors.secondaryForeground);
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--accent-foreground', colors.accentForeground);
    root.style.setProperty('--muted', colors.muted);
    root.style.setProperty('--muted-foreground', colors.mutedForeground);
    root.style.setProperty('--card', colors.card);
    root.style.setProperty('--card-foreground', colors.cardForeground);
    root.style.setProperty('--border', colors.border);
    root.style.setProperty('--input', colors.input);
    root.style.setProperty('--ring', colors.ring);
    root.style.setProperty('--gradient-hero', colors.gradientHero);
    root.style.setProperty('--gradient-card', colors.gradientCard);
    root.style.setProperty('--shadow-sm', colors.shadowSm);
    root.style.setProperty('--shadow-card', colors.shadowCard);
    root.style.setProperty('--shadow-lg', colors.shadowLg);

    localStorage.setItem('checkfirst-theme', theme);
  }, [theme]);

  const setTheme = (newTheme: ThemeName) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}
